import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useAuth } from "src/contexts/auth-provider";
import PayBillModal from "src/features/OrderManagement/components/PayBillModal/PayBillModal";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GET_CUSTOMER_ORDER_FOR_EMPLOYEE } from "src/constants/queries";
import {
  CAPTURE_PAYMENT,
  MARK_ENTERPRISE_DISPATCH_SLIPS_PRINTED,
  UPDATE_CUSTOMER_ORDER,
} from "src/constants/mutations";
import ActiveOrderDetailsPanel from "src/features/ActiveOrders/components/ActiveOrderDetailsPanel";
import ActiveOrdersTable from "src/features/ActiveOrders/components/ActiveOrdersTable";
import ActiveOrdersHeader from "src/features/ActiveOrders/components/ActiveOrdersHeader";
import EnterprisePackageSlipPrintView from "src/features/ActiveOrders/components/EnterprisePackageSlipPrintView";
import Spinner from "src/components/common/Spinner";
import OrderDetailsDrawer, {
  PrintOptionsModal,
  PrintOptionsSelection,
} from "src/components/common/OrderDetailsDrawer";
import { BatchUpdateStatusPickerModal } from "src/components/common/BatchUpdateStatusPickerModal";
import { BatchUpdateProgressModal } from "src/components/common/BatchUpdateProgressModal";
import {
  BatchOperationSummaryModal,
  type BatchOperationLineResult,
} from "src/components/common/BatchOperationSummaryModal";
import { CustomerOrder } from "src/interfaces/order.interface";
import {
  OrderSourceEnum,
  OrderStatusEnum,
  OrderTypeEnum,
  ReceiptTypeEnum,
} from "src/enums/order.enum";
import { useWebSocketSender } from "src/hooks/useSendMassageToWs";
import { failureToast, successToast } from "src/utils/toasts";
import { isEligibleForBatchCapture } from "src/features/ActiveOrders/utils/batchCaptureEligibility";
import { hasBatchOperationsAccess } from "src/utils/batchOrderOperationsAccess";
import { getOrderStatusStyle } from "src/constants/orderStatusBadges";
import { orderDrawerPrimaryButtonClass } from "src/constants/orderDrawerButtons";

interface ActiveOrdersPageProps {
  hideTitle?: boolean;
  /** When provided, header is hidden and search/filter use this state (unified control bar). */
  controlledSearchQuery?: string;
  onSearchChange?: (value: string) => void;
  controlledOrderTypeFilter?: OrderTypeEnum | null;
  onOrderTypeFilterChange?: (type: OrderTypeEnum | null) => void;
}

const ActiveOrdersPage = ({
  hideTitle,
  controlledSearchQuery,
  onSearchChange,
  controlledOrderTypeFilter,
  onOrderTypeFilterChange,
}: ActiveOrdersPageProps) => {
  const [showCreateOrderModal, setShowCreateOrderModal] = useState<boolean>(false);
  const [showSelectedCustomerOrderDetails, setShowSelectedCustomerOrderDetails] = useState<boolean>(false);
  const [isLoadingCustomerOrders, setIsLoadingCustomerOrders] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<CustomerOrder | undefined>();
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  const [internalSearch, setInternalSearch] = useState<string>("");
  const [internalOrderType, setInternalOrderType] = useState<OrderTypeEnum | null>(null);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [packageSlipOrders, setPackageSlipOrders] = useState<CustomerOrder[]>([]);
  const [packageSlipPrintQueued, setPackageSlipPrintQueued] = useState(false);
  const packageSlipPrintRef = useRef<HTMLDivElement>(null);
  const packageSlipPrintedIdsRef = useRef<string[]>([]);

  const searchOrderQuery = controlledSearchQuery !== undefined ? controlledSearchQuery : internalSearch;
  const orderTypeFilter = controlledOrderTypeFilter !== undefined ? controlledOrderTypeFilter : internalOrderType;
  const useControlBar = controlledSearchQuery !== undefined && onSearchChange != null;

  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant, token, userRole } = useAuth();
  const canBatchUpdate = hasBatchOperationsAccess(userRole);

  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(() => new Set());
  const [batchPickerOpen, setBatchPickerOpen] = useState(false);
  const [batchProgressOpen, setBatchProgressOpen] = useState(false);
  const [batchProgressMessage, setBatchProgressMessage] = useState("");
  const [batchResultsOpen, setBatchResultsOpen] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchOperationLineResult[]>([]);
  const [batchResultsOperationName, setBatchResultsOperationName] = useState("Batch update status");
  const { isSending, startConnection, stopConnection, sendMessage } = useWebSocketSender();

  useEffect(() => {
    if (token) startConnection(token);
    return () => stopConnection();
  }, [token, startConnection, stopConnection]);

  const getActiveCustomerOrders = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setIsLoadingCustomerOrders(true);
    setLoadError(null);
    try {
      const { data: customerOrdersResponse } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_CUSTOMER_ORDER_FOR_EMPLOYEE,
        variables: { storeCorrelationId: hasRestaurant.correlationId },
      });
      const responseErrors = customerOrdersResponse?.errors;
      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
        const message =
          responseErrors[0]?.message ??
          "Failed to load active orders.";
        setLoadError(message);
        setCustomerOrders([]);
        return;
      }
      const raw = customerOrdersResponse?.data?.activeCustomerOrdersByStoreCorrelationId || [];
      const list: CustomerOrder[] = Array.isArray(raw) ? raw : [];
      setCustomerOrders(list);
      // Keep order details drawer in sync after capture / status update (same correlationId, fresh payload).
      setSelectedCustomerOrder((prev) => {
        if (!prev?.correlationId) return prev;
        const fresh = list.find((o) => o.correlationId === prev.correlationId);
        return fresh ?? prev;
      });
    } catch (error) {
      console.error(error);
      setLoadError(
        error instanceof Error ? error.message : "Failed to load active orders."
      );
    } finally {
      setIsLoadingCustomerOrders(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  const markEnterpriseDispatchSlipsPrinted = useCallback(async (customerOrderCorrelationIds: string[]) => {
    const storeCorrelationId = hasRestaurant?.correlationId;
    if (!storeCorrelationId || customerOrderCorrelationIds.length === 0) return;
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: MARK_ENTERPRISE_DISPATCH_SLIPS_PRINTED,
        variables: {
          input: {
            storeCorrelationId,
            customerOrderCorrelationIds,
          },
        },
      });
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        failureToast(data.errors[0]?.message ?? "Package slips printed, but print tracking failed.");
        return;
      }
      await getActiveCustomerOrders();
    } catch (error) {
      console.error(error);
      failureToast("Package slips printed, but print tracking failed.");
    }
  }, [axBe, getActiveCustomerOrders, hasRestaurant?.correlationId]);

  const printEnterprisePackageSlips = useReactToPrint({
    contentRef: packageSlipPrintRef,
    documentTitle: "enterprise-package-slips",
    onAfterPrint: () => {
      const printedIds = packageSlipPrintedIdsRef.current;
      packageSlipPrintedIdsRef.current = [];
      setPackageSlipPrintQueued(false);
      if (printedIds.length > 0) {
        successToast(`Package slip${printedIds.length === 1 ? "" : "s"} sent to printer.`);
        void markEnterpriseDispatchSlipsPrinted(printedIds);
      }
    },
  });

  useEffect(() => {
    getActiveCustomerOrders();
  }, [getActiveCustomerOrders]);

  const searchOrders = useCallback(
    (orderQuery: string) => {
      if (onSearchChange) onSearchChange(orderQuery);
      else setInternalSearch(orderQuery);
    },
    [onSearchChange]
  );

  const handleOrderTypeFilter = useCallback(
    (type: OrderTypeEnum | null) => {
      if (onOrderTypeFilterChange) onOrderTypeFilterChange(type);
      else setInternalOrderType(type);
    },
    [onOrderTypeFilterChange]
  );

  useEffect(() => {
    setSelectedOrderIds(new Set());
  }, [searchOrderQuery, orderTypeFilter]);

  const toggleOrderSelected = useCallback((correlationId: string, selected: boolean) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(correlationId);
      else next.delete(correlationId);
      return next;
    });
  }, []);

  const selectAllVisibleOrders = useCallback((select: boolean, visibleCorrelationIds: string[]) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (select) visibleCorrelationIds.forEach((id) => next.add(id));
      else visibleCorrelationIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const runBatchStatusUpdate = useCallback(
    async (status: OrderStatusEnum) => {
      const storeId = hasRestaurant?.correlationId;
      if (!storeId) return;

      setBatchPickerOpen(false);
      const ids = Array.from(selectedOrderIds);
      const statusLabel = getOrderStatusStyle(status).label;
      setBatchProgressMessage(`Updating ${ids.length} order(s) to ${statusLabel}…`);
      setBatchProgressOpen(true);

      const results: BatchOperationLineResult[] = await Promise.all(
        ids.map(async (correlationId) => {
          try {
            const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
              query: UPDATE_CUSTOMER_ORDER,
              variables: {
                updateCustomerOrderInput: {
                  storeCorrelationId: storeId,
                  customerOrderCorrelationId: correlationId,
                  orderStatus: status,
                  orderSource: OrderSourceEnum.RESTAURANT_WEB_APP,
                },
              },
            });
            if (data?.data?.updateCustomerOrder) {
              return { correlationId, success: true };
            }
            const msg = data?.errors?.[0]?.message ?? "Update failed.";
            return { correlationId, success: false, errorMessage: msg };
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "Request failed.";
            return { correlationId, success: false, errorMessage: msg };
          }
        })
      );

      setBatchProgressOpen(false);
      setBatchResultsOperationName("Batch update status");
      setBatchResults(results);
      setBatchResultsOpen(true);
      setSelectedOrderIds(new Set());
      await getActiveCustomerOrders();
    },
    [axBe, getActiveCustomerOrders, hasRestaurant?.correlationId, selectedOrderIds]
  );

  const batchCapturableCount = useMemo(() => {
    if (!canBatchUpdate || selectedOrderIds.size === 0) return 0;
    return customerOrders.filter(
      (o) => o.correlationId && selectedOrderIds.has(o.correlationId) && isEligibleForBatchCapture(o)
    ).length;
  }, [canBatchUpdate, customerOrders, selectedOrderIds]);

  const runBatchCapture = useCallback(async () => {
    const storeId = hasRestaurant?.correlationId;
    if (!storeId) return;

    const ordersToProcess = customerOrders.filter(
      (o) => o.correlationId && selectedOrderIds.has(o.correlationId) && isEligibleForBatchCapture(o)
    );

    if (ordersToProcess.length === 0) {
      const n = selectedOrderIds.size;
      failureToast(
        n === 0
          ? "No orders selected."
          : "None of the selected orders can be batch-captured. Requires non-channel orders with Authorized payment (AUTH / capture later)."
      );
      return;
    }

    setBatchProgressMessage(`Capturing ${ordersToProcess.length} order(s)…`);
    setBatchProgressOpen(true);

    const results: BatchOperationLineResult[] = await Promise.all(
      ordersToProcess.map(async (order) => {
        const correlationId = order.correlationId!;
        try {
          const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
            query: CAPTURE_PAYMENT,
            variables: {
              captureCustomerOrderPaymentInput: {
                storeCorrelationId: storeId,
                customerOrderCorrelationId: correlationId,
              },
            },
          });
          if (data?.data?.captureCustomerOrderPayment) {
            return { correlationId, success: true };
          }
          const msg = data?.errors?.[0]?.message ?? "Failed to capture payment.";
          return { correlationId, success: false, errorMessage: msg };
        } catch (e: unknown) {
          return {
            correlationId,
            success: false,
            errorMessage: e instanceof Error ? e.message : "Request failed.",
          };
        }
      })
    );

    setBatchProgressOpen(false);
    setBatchResultsOperationName("Batch Capture");
    setBatchResults(results);
    setBatchResultsOpen(true);
    setSelectedOrderIds(new Set());
    await getActiveCustomerOrders();
  }, [axBe, customerOrders, getActiveCustomerOrders, hasRestaurant?.correlationId, selectedOrderIds]);

  const filteredCustomerOrders = useMemo(() => {
    if (!customerOrders.length) return [];
    const q = searchOrderQuery.trim().toLowerCase();
    const type = orderTypeFilter?.trim().toLowerCase();
    return customerOrders.filter((order: CustomerOrder) => {
      const matchesSearch =
        !q ||
        order.customerFirstName?.toLowerCase().includes(q) ||
        order.customerEmail?.toLowerCase().includes(q) ||
        order.customerPhoneNumber?.includes(searchOrderQuery) ||
        order.correlationId?.toLowerCase().includes(q);
      const matchesType = !type || order.orderType?.toLowerCase().includes(type);
      return matchesSearch && matchesType;
    });
  }, [customerOrders, searchOrderQuery, orderTypeFilter]);

  const visibleEnterpriseOrders = useMemo(
    () => filteredCustomerOrders.filter((order) => order.orderType === OrderTypeEnum.ENTERPRISE),
    [filteredCustomerOrders]
  );

  const selectedEnterpriseOrders = useMemo(
    () =>
      visibleEnterpriseOrders.filter(
        (order) => order.correlationId && selectedOrderIds.has(order.correlationId)
      ),
    [selectedOrderIds, visibleEnterpriseOrders]
  );

  const enterprisePackageSlipTargetCount = useMemo(() => {
    if (orderTypeFilter !== OrderTypeEnum.ENTERPRISE) return 0;
    if (selectedEnterpriseOrders.length > 0) return selectedEnterpriseOrders.length;
    return visibleEnterpriseOrders.filter((order) => !order.enterpriseDispatchSlipPrintedAt).length;
  }, [orderTypeFilter, selectedEnterpriseOrders.length, visibleEnterpriseOrders]);

  const handlePrintEnterprisePackageSlips = useCallback(() => {
    const targets =
      selectedEnterpriseOrders.length > 0
        ? selectedEnterpriseOrders
        : visibleEnterpriseOrders.filter((order) => !order.enterpriseDispatchSlipPrintedAt);

    if (targets.length === 0) {
      failureToast("No new enterprise package slips to print.");
      return;
    }

    packageSlipPrintedIdsRef.current = targets
      .map((order) => order.correlationId)
      .filter((id): id is string => Boolean(id));
    setPackageSlipOrders(targets);
    setPackageSlipPrintQueued(true);
  }, [selectedEnterpriseOrders, visibleEnterpriseOrders]);

  useEffect(() => {
    if (!packageSlipPrintQueued || packageSlipOrders.length === 0) return;
    const timeoutId = window.setTimeout(() => {
      printEnterprisePackageSlips();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [packageSlipOrders.length, packageSlipPrintQueued, printEnterprisePackageSlips]);

  const handleRowClick = useCallback((order: CustomerOrder) => {
    setSelectedCustomerOrder(order);
    setShowSelectedCustomerOrderDetails(true);
  }, []);

  const handlePayBill = useCallback((order: CustomerOrder) => {
    setSelectedCustomerOrder(order);
    setShowCreateOrderModal(true);
  }, []);

  const handlePrint = useCallback((order: CustomerOrder) => {
    setSelectedCustomerOrder(order);
    setShowSelectedCustomerOrderDetails(true);
  }, []);

  const handleEdit = useCallback((order: CustomerOrder) => {
    setSelectedCustomerOrder(order);
    setShowSelectedCustomerOrderDetails(true);
  }, []);

  const handlePrintFromDrawer = useCallback(() => {
    setPrintOptionsOpen(true);
  }, []);

  const handleConfirmPrintOptions = useCallback((options: PrintOptionsSelection) => {
    if (!selectedCustomerOrder) return;
    const receiptTypes: ReceiptTypeEnum[] = [];
    if (options.sendToKitchen) receiptTypes.push(ReceiptTypeEnum.KITCHEN);
    if (options.restaurantCopy) receiptTypes.push(ReceiptTypeEnum.RESTAURANT);
    if (options.customerCopy) receiptTypes.push(ReceiptTypeEnum.CUSTOMER);

    if (receiptTypes.length === 0) {
      failureToast("Please select at least one print option.");
      return;
    }

    let sentCount = 0;
    receiptTypes.forEach((receiptType) => {
      const sent = sendMessage("/app/manualPrinter", {
        customerOrderCorrelationId: selectedCustomerOrder.correlationId,
        receiptType,
      });
      if (sent) sentCount += 1;
    });

    if (sentCount === 0) {
      failureToast("Printer connection is not ready. Please try again.");
      return;
    }

    setPrintOptionsOpen(false);
    successToast("Print request sent.");
  }, [selectedCustomerOrder, sendMessage]);

  const handlePayFromDrawer = useCallback(() => {
    setShowSelectedCustomerOrderDetails(false);
    setShowCreateOrderModal(true);
  }, []);

  return (
    <div className="w-full flex-1 min-h-0 flex flex-col min-w-0">
      {!useControlBar && (
        <div className="flex justify-between items-center flex-wrap gap-2 mb-4 shrink-0">
          {!hideTitle ? (
            <h1 className="text-lg font-semibold text-[#111827]">Active Orders</h1>
          ) : (
            <span />
          )}
          <ActiveOrdersHeader
            searchOrders={searchOrders}
            orderTypeFilter={orderTypeFilter}
            onOrderTypeFilter={handleOrderTypeFilter}
          />
        </div>
      )}

      <div
        className="flex-1 min-h-0 flex flex-col min-w-0"
        aria-label="Active orders table"
      >
        {isLoadingCustomerOrders ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <p className="text-[#b42318] font-medium">Failed to load active orders.</p>
            <p className="max-w-xl text-sm text-[#6b7280]">{loadError}</p>
            <button
              type="button"
              className={orderDrawerPrimaryButtonClass}
              onClick={() => void getActiveCustomerOrders()}
            >
              Retry
            </button>
          </div>
        ) : filteredCustomerOrders.length === 0 ? (
          <p className="text-[#9B9B9B] py-12 text-center">No active orders found.</p>
        ) : (
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <ActiveOrdersTable
              customerOrders={filteredCustomerOrders}
              onRowClick={handleRowClick}
              onPayBill={handlePayBill}
              onPrint={handlePrint}
              onEdit={handleEdit}
              batchSelection={
                canBatchUpdate
                  ? {
                      selectedIds: selectedOrderIds,
                      onToggle: toggleOrderSelected,
                      onSelectAll: selectAllVisibleOrders,
                    }
                  : undefined
              }
              toolbar={
                canBatchUpdate ? (
                  <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#f8fafc] border-b border-[#e5e7eb]">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm text-[#374151]">
                        {selectedOrderIds.size === 0
                          ? "0 orders selected"
                          : `${selectedOrderIds.size} order${selectedOrderIds.size === 1 ? "" : "s"} selected`}
                      </span>
                      {selectedOrderIds.size > 0 ? (
                        <span className="text-xs text-[#6b7280]">
                          {batchCapturableCount === 0
                            ? "Batch capture: none of the selected orders are eligible (need Authorized AUTH card payment, non-channel)."
                            : `${batchCapturableCount} eligible for batch capture.`}
                        </span>
                      ) : null}
                    </div>
                    <span className="flex-1 min-w-[1rem]" aria-hidden />
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      {orderTypeFilter === OrderTypeEnum.ENTERPRISE && (
                        <button
                          type="button"
                          disabled={enterprisePackageSlipTargetCount === 0 || packageSlipPrintQueued}
                          onClick={handlePrintEnterprisePackageSlips}
                          className={orderDrawerPrimaryButtonClass}
                        >
                          Print package slips
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={batchCapturableCount === 0 || batchProgressOpen}
                        title={
                          batchCapturableCount === 0 && selectedOrderIds.size > 0
                            ? "Selected orders are not eligible: need Authorized payment in AUTH mode and not an external channel order."
                            : undefined
                        }
                        onClick={() => void runBatchCapture()}
                        className={orderDrawerPrimaryButtonClass}
                      >
                        Batch capture
                      </button>
                      <button
                        type="button"
                        disabled={selectedOrderIds.size === 0 || batchProgressOpen}
                        onClick={() => setBatchPickerOpen(true)}
                        className={orderDrawerPrimaryButtonClass}
                      >
                        Batch update status
                      </button>
                    </div>
                  </div>
                ) : undefined
              }
            />
          </div>
        )}
      </div>

      {showCreateOrderModal && selectedCustomerOrder && (
        <PayBillModal
          getActiveCustomerOrders={getActiveCustomerOrders}
          onClose={() => setShowCreateOrderModal(false)}
          customerOrder={selectedCustomerOrder}
        />
      )}

      <BatchUpdateStatusPickerModal
        open={batchPickerOpen}
        onClose={() => setBatchPickerOpen(false)}
        onApply={(status) => void runBatchStatusUpdate(status)}
      />
      <BatchUpdateProgressModal open={batchProgressOpen} message={batchProgressMessage} />
      <BatchOperationSummaryModal
        open={batchResultsOpen}
        operationName={batchResultsOperationName}
        results={batchResults}
        onClose={() => setBatchResultsOpen(false)}
      />

      <OrderDetailsDrawer
        open={showSelectedCustomerOrderDetails && !!selectedCustomerOrder}
        onClose={() => {
          setShowSelectedCustomerOrderDetails(false);
          setPrintOptionsOpen(false);
        }}
        title="Order Details"
      >
        {selectedCustomerOrder && (
          <ActiveOrderDetailsPanel
            customerOrder={selectedCustomerOrder}
            onClose={() => setShowSelectedCustomerOrderDetails(false)}
            onEdit={() => {}}
            onPay={handlePayFromDrawer}
            onPrint={handlePrintFromDrawer}
            onOrderUpdated={getActiveCustomerOrders}
          />
        )}
      </OrderDetailsDrawer>
      <PrintOptionsModal
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        onConfirm={handleConfirmPrintOptions}
        isSending={isSending}
      />
      <EnterprisePackageSlipPrintView ref={packageSlipPrintRef} orders={packageSlipOrders} />
    </div>
  );
};

export default ActiveOrdersPage;
